import { inject, Injectable, NgZone, signal } from '@angular/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { SplashScreen } from '@capacitor/splash-screen';
import { OtaOverlayMode, OtaUpdateService } from './ota-update.service';

const UPDATE_STUCK_TIMEOUT_MS = 60_000;
const FADE_OUT_MS = 300;

@Injectable()
export class NativeOtaUpdateService extends OtaUpdateService {
  private zone = inject(NgZone);
  private readonly modeState = signal<OtaOverlayMode>('hidden');
  private readonly fadingOutState = signal(false);
  private readonly downloadPercentState = signal(0);
  private hideFallbackTimeout: ReturnType<typeof setTimeout> | undefined;

  readonly mode = this.modeState.asReadonly();
  readonly isFadingOut = this.fadingOutState.asReadonly();
  readonly downloadPercent = this.downloadPercentState.asReadonly();

  init(): void {
    // The app is revealed as soon as it paints rather than waiting for Capgo's
    // noNeedUpdate — an update overlay is shown only once one actually starts.
    requestAnimationFrame(() => SplashScreen.hide({ fadeOutDuration: 200 }));

    CapacitorUpdater.addListener('noNeedUpdate', () =>
      this.zone.run(() => this.hideOverlay()),
    );
    CapacitorUpdater.addListener('updateAvailable', () =>
      this.zone.run(() => this.showUpdating()),
    );
    CapacitorUpdater.addListener('download', ({ percent }) =>
      this.zone.run(() => {
        this.showUpdating();
        this.downloadPercentState.set(percent);
      }),
    );
    CapacitorUpdater.addListener('downloadComplete', () =>
      this.zone.run(() => {
        this.downloadPercentState.set(100);
        this.modeState.set('restarting');
      }),
    );
    CapacitorUpdater.addListener('downloadFailed', () =>
      this.zone.run(() => this.hideOverlay()),
    );
    CapacitorUpdater.addListener('appReloaded', () =>
      this.zone.run(() => this.hideOverlay()),
    );
  }

  private showUpdating(): void {
    if (this.modeState() === 'updating') {
      return;
    }
    this.modeState.set('updating');
    this.fadingOutState.set(false);
    this.scheduleHideFallback(UPDATE_STUCK_TIMEOUT_MS);
  }

  private hideOverlay(): void {
    if (this.modeState() === 'hidden' || this.fadingOutState()) {
      return;
    }
    clearTimeout(this.hideFallbackTimeout);
    this.fadingOutState.set(true);
    setTimeout(() => {
      this.zone.run(() => {
        this.modeState.set('hidden');
        this.fadingOutState.set(false);
      });
    }, FADE_OUT_MS);
  }

  private scheduleHideFallback(delayMs: number): void {
    clearTimeout(this.hideFallbackTimeout);
    this.hideFallbackTimeout = setTimeout(
      () => this.zone.run(() => this.hideOverlay()),
      delayMs,
    );
  }
}
